import React, { useState, useEffect } from 'react'
import { Modal, Table, Tag, Image, Descriptions, message, Button } from 'antd'
import { ApiRequest } from '../../../../services/api/api'
import { modalStyles, titleStyle, buttonStyles, modalContainerStyle } from './modal-styles'
import moment from 'moment'

interface GetEmployeeDto {
  employeeId: number
  employeeCode?: string
  firstName?: string
  lastName?: string
  userName?: string
  email?: string
  mobile?: string
  deptId?: number
  roleId?: number
}

interface GetPurchaseOrderDto2 {
  poId: number
  poNo: string
  price?: number
  gst?: number
  inclGstPrice?: number
  deliveryAddr?: string
  deliveryDate?: string
  deliveryInstruction?: string
  telNo?: string
  suplierId?: number
  currencyId?: number
  poStatusId?: number
}

interface GetUnloadingDetailDto {
  inspectionId: string
  rawMaterialId: number
  detailId: string
  revQuantity: number
  revAreaQty: number | null
  putAwayQty: number | null
  handledQuantity: number | null
  palletQuantity: number | null
  dispatchedQuantity: number | null
  note: string | null
  createdBy: string | null
  createdAt: string | null
  updatedAt: string | null
  no: string | null
  // Material object - should be included by backend
  rawMaterial?: {
    rawMaterialId: number
    rawMaterialCode: string
    rawMaterialName: string
  }
  // Additional fields for the table
  containerNo?: string
  arrivalTime?: string
  receiver?: GetEmployeeDto | null
  notes?: string
  conditionOfProduct?: string
  conditionOfPackage?: string
  images?: string | null
}

interface GetUnloadingInspectionLogDto {
  inspectionId: string
  containerNo: string
  arrivalTime: string | null
  unloadingDate: string | null
  unloadingPeople: string
  receiverId: number | null
  spendingHours: number
  conditionOfProduct: string
  conditionOfPackage: string
  actualQty: number
  notes: string
  takeAwayDate: string | null
  poId: number | null
  active: boolean | null
  images: string | null
  status: number | null
  no: string | null
  receiver: GetEmployeeDto | null
  po: GetPurchaseOrderDto2 | null
  unloadingDetail: GetUnloadingDetailDto[]
}

interface InspectionLogModalProps {
  visible: boolean
  onClose: () => void
  inspectionId: string
}

const InspectionLogModal: React.FC<InspectionLogModalProps> = ({
  visible,
  onClose,
  inspectionId
}) => {
  const [loading, setLoading] = useState(false)
  const [logData, setLogData] = useState<GetUnloadingInspectionLogDto | null>(null)
  const [parentRecords, setParentRecords] = useState<any[]>([])
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')

  useEffect(() => {
    if (visible && inspectionId) {
      fetchInspectionLog()
    }
  }, [visible, inspectionId])

  const fetchInspectionLog = async () => {
    setLoading(true)
    try {
      const response = await ApiRequest({
        url: `UnloadingInspection/GetUnloadingInspectionLog?inspectionId=${inspectionId}`,
        method: 'get',
        isShowSpinner: true
      })

      if (response?.data?.data) {
        let data = response.data.data
        let parentRecordsList: any[] = []

        console.log('Raw API response:', response.data.data)

        // Case 1: data is an array of inspection records
        if (Array.isArray(data) && data.length > 0) {
          console.log('Data is array with', data.length, 'records')

          // Process each inspection record in the array
          data.forEach((inspectionData: any) => {
            // Check if unloadingDetail is in nested structure
            let unloadingDetails = inspectionData.unloadingDetail

            if (!unloadingDetails && inspectionData?.po?.unloadingInspection) {
              // Get all unloading inspections and their details
              const inspections = inspectionData.po.unloadingInspection
              if (Array.isArray(inspections) && inspections.length > 0) {
                // Collect all unloadingDetails from all inspections
                unloadingDetails = inspections.flatMap((inspection: any) =>
                  inspection.unloadingDetail || []
                )
                console.log('Found', inspections.length, 'inspections with total', unloadingDetails.length, 'details')
              }
            }

            // Create parent record if it has details
            if (unloadingDetails && Array.isArray(unloadingDetails) && unloadingDetails.length > 0) {
              // Sort unloadingDetails by Item Code (rawMaterialCode) for consistent display
              const sortedDetails = [...unloadingDetails].sort((a: any, b: any) => {
                const codeA = (a.rawMaterial?.rawMaterialCode || '').toString().toUpperCase().trim()
                const codeB = (b.rawMaterial?.rawMaterialCode || '').toString().toUpperCase().trim()
                return codeA.localeCompare(codeB)
              })

              parentRecordsList.push({
                key: inspectionData.inspectionId,
                inspectionId: inspectionData.inspectionId,
                containerNo: inspectionData.containerNo,
                arrivalTime: inspectionData.arrivalTime,
                receiver: inspectionData.receiver,
                notes: inspectionData.notes,
                conditionOfProduct: inspectionData.conditionOfProduct,
                conditionOfPackage: inspectionData.conditionOfPackage,
                images: inspectionData.images,
                status: inspectionData.status,
                unloadingDetail: sortedDetails
              })
            }
          })

          // Use the first record for logData (for backward compatibility)
          data = data[0]
        }
        // Case 2: data is a single object with nested unloadingInspection array
        else if (data?.po?.unloadingInspection && Array.isArray(data.po.unloadingInspection)) {
          console.log('Single record with', data.po.unloadingInspection.length, 'inspections')

          // Process each unloading inspection as a separate parent record
          data.po.unloadingInspection.forEach((inspection: any) => {
            if (inspection.unloadingDetail && Array.isArray(inspection.unloadingDetail) && inspection.unloadingDetail.length > 0) {
              // Sort unloadingDetails by Item Code (rawMaterialCode) for consistent display
              const sortedDetails = [...inspection.unloadingDetail].sort((a: any, b: any) => {
                const codeA = a.rawMaterial?.rawMaterialCode || ''
                const codeB = b.rawMaterial?.rawMaterialCode || ''
                return codeA.localeCompare(codeB)
              })

              parentRecordsList.push({
                key: inspection.inspectionId,
                inspectionId: inspection.inspectionId,
                containerNo: inspection.containerNo || data.containerNo,
                arrivalTime: inspection.arrivalTime || data.arrivalTime,
                receiver: inspection.receiver || data.receiver,
                notes: inspection.notes || data.notes,
                conditionOfProduct: inspection.conditionOfProduct || data.conditionOfProduct,
                conditionOfPackage: inspection.conditionOfPackage || data.conditionOfPackage,
                images: inspection.images || data.images,
                status: inspection.status || data.status,
                unloadingDetail: sortedDetails
              })
            }
          })
        }
        // Case 3: data is a single object with direct unloadingDetail
        else if (data.unloadingDetail && Array.isArray(data.unloadingDetail)) {
          console.log('Single record with direct unloadingDetail')

          // Sort unloadingDetails by Item Code (rawMaterialCode) for consistent display
          const sortedDetails = [...data.unloadingDetail].sort((a: any, b: any) => {
            const codeA = a.rawMaterial?.rawMaterialCode || ''
            const codeB = b.rawMaterial?.rawMaterialCode || ''
            return codeA.localeCompare(codeB)
          })

          parentRecordsList.push({
            key: data.inspectionId,
            inspectionId: data.inspectionId,
            containerNo: data.containerNo,
            arrivalTime: data.arrivalTime,
            receiver: data.receiver,
            notes: data.notes,
            conditionOfProduct: data.conditionOfProduct,
            conditionOfPackage: data.conditionOfPackage,
            images: data.images,
            status: data.status,
            unloadingDetail: sortedDetails
          })
        }

        console.log('Created', parentRecordsList.length, 'parent records')
        setParentRecords(parentRecordsList)

        // Debug logging
        console.log('Final data structure:', data)
        console.log('Parent records:', parentRecordsList)

        setLogData(data)

        // Parse images
        if (data.images && typeof data.images === 'string') {
          try {
            const parsedImages = JSON.parse(data.images)
            if (Array.isArray(parsedImages)) {
              const urls = parsedImages
                .filter((item: any) => item && item.url)
                .map((item: any) => item.url)
              setImageUrls(urls)
            }
          } catch (e) {
            console.error('Failed to parse images:', e)
            setImageUrls([])
          }
        } else {
          setImageUrls([])
        }
      } else {
        console.log('No data in response:', response)
      }
    } catch (error) {
      console.error('Failed to fetch inspection log:', error)
      message.error('Failed to fetch inspection log')
    } finally {
      setLoading(false)
    }
  }

  const getStatusTag = (statusCode: number | null | undefined) => {
    const statusMap: Record<number, { color: string; text: string }> = {
      0: { color: '#9ca3af', text: 'Pending' },
      1: { color: '#3b82f6', text: 'In Progress' },
      2: { color: '#10b981', text: 'Completed' },
      3: { color: '#ef4444', text: 'Cancelled' }
    }
    const statusConfig = statusMap[statusCode ?? 0] || statusMap[0]
    return <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
  }

  const handlePreview = (url: string, title?: string) => {
    setPreviewImage(url)
    setPreviewTitle(title || 'Photo Preview')
    setPreviewVisible(true)
  }

  const parseImageUrls = (value: string | null): string[] => {
    if (!value || typeof value !== 'string') return []
    try {
      const parsedImages = JSON.parse(value)
      if (Array.isArray(parsedImages)) {
        return parsedImages.filter((item: any) => item && item.url).map((item: any) => item.url)
      }
    } catch (e) {
      console.error('Failed to parse images:', e)
    }
    return []
  }

  const detailColumns = [
    {
      title: 'Item Code',
      key: 'itemCode',
      width: 150,
      render: (_: any, record: GetUnloadingDetailDto) => (
        <span style={{ fontWeight: 500, color: modalStyles.textPrimary }}>
          {record.rawMaterial?.rawMaterialCode || `ID: ${record.rawMaterialId}` || 'N/A'}
        </span>
      )
    },
    {
      title: 'Description',
      key: 'description',
      width: 300,
      ellipsis: { showTitle: false },
      render: (_: any, record: GetUnloadingDetailDto) => (
        <span style={{ fontSize: '13px', color: modalStyles.textSecondary }}>
          {record.rawMaterial?.rawMaterialName || 'No description'}
        </span>
      )
    },
    {
      title: 'qty in inspection',
      key: 'receivingQty',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: GetUnloadingDetailDto) => {
        const qty = record.revQuantity ?? record.handledQuantity ?? 0
        return (
          <span style={{ fontSize: '14px', fontWeight: 600, color: modalStyles.success }}>
            {qty}
          </span>
        )
      }
    }
  ]

  // Parent table columns
  const parentColumns = [
    {
      title: 'Packing Slip No.',
      dataIndex: 'containerNo',
      key: 'containerNo',
      width: 180,
      render: (value: string) => (
        <span style={{ fontWeight: 600, color: modalStyles.textPrimary }}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      title: 'Arrival Time',
      dataIndex: 'arrivalTime',
      key: 'arrivalTime',
      width: 160,
      render: (value: string) => (
        <span style={{ fontSize: '13px', color: modalStyles.textPrimary }}>
          {value ? moment.utc(value).local().format('DD/MM/YYYY HH:mm') : 'N/A'}
        </span>
      )
    },
    {
      title: 'Receiver',
      dataIndex: 'receiver',
      key: 'receiver',
      width: 140,
      render: (value: GetEmployeeDto | null) => (
        <span style={{ fontSize: '13px', color: modalStyles.textPrimary }}>
          {value
            ? `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.userName || value.employeeCode || 'N/A'
            : 'N/A'}
        </span>
      )
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      width: 250,
      ellipsis: { showTitle: true },
      render: (value: string, record: any) => {
        const note = value || record.conditionOfProduct || record.conditionOfPackage || 'N/A'
        return (
          <span style={{ fontSize: '13px', color: modalStyles.textSecondary }} title={note}>
            {note}
          </span>
        )
      }
    },
    {
      title: 'Photos',
      dataIndex: 'images',
      key: 'photos',
      width: 100,
      align: 'center' as const,
      render: (value: string | null, record: any) => {
        const urls = parseImageUrls(value)
        const imageCount = urls.length
        if (imageCount === 0) {
          return <span style={{ fontSize: '13px', color: modalStyles.textSecondary }}>-</span>
        }
        return (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {urls.slice(0, 3).map((url, index) => (
              <Image
                key={index}
                src={url}
                alt={`Photo ${index + 1}`}
                width={30}
                height={30}
                style={{
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${modalStyles.borderSecondary}`
                }}
                preview={{
                  visible: previewVisible && previewImage === url,
                  onVisibleChange: (vis) => {
                    if (!vis) {
                      setPreviewVisible(false)
                    }
                  }
                }}
                onClick={() => handlePreview(url, `${record.containerNo} - Photo ${index + 1}`)}
              />
            ))}
            {imageCount > 3 && (
              <span style={{
                fontSize: '12px',
                color: modalStyles.info,
                fontWeight: 600,
                padding: '4px 8px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px'
              }}>
                +{imageCount - 3}
              </span>
            )}
          </div>
        )
      }
    },
    {
      title: 'Items',
      key: 'itemCount',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Tag color={modalStyles.primary} style={{ margin: 0 }}>
          {record.unloadingDetail?.length || 0}
        </Tag>
      )
    }
  ]

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      closable={true}
      style={{ top: 20 }}
      bodyStyle={{ padding: modalStyles.padding, maxHeight: 'calc(100vh - 100px)', overflow: 'hidden' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          Receiving Log
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : logData ? (
          <>
            {/* 简化的头部信息 */}
            <div style={{
              backgroundColor: modalStyles.bgWhite,
              border: `1px solid ${modalStyles.borderSecondary}`,
              borderRadius: modalStyles.borderRadiusSmall,
              padding: modalStyles.paddingSmall,
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: modalStyles.textSecondary, marginRight: '8px' }}>PO:</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: modalStyles.textPrimary }}>
                      {logData.po?.poNo || 'N/A'}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: modalStyles.textSecondary }}>
                  {parentRecords.length > 0 ? (
                    <>Shipments: {parentRecords.length} | Total Items: {parentRecords.reduce((sum: number, p: any) => sum + (p.unloadingDetail?.length || 0), 0)}</>
                  ) : (
                    'No records found'
                  )}
                </div>
              </div>
            </div>

            {/* 主要表格 - 可滚动 */}
            <div style={{
              backgroundColor: modalStyles.bgWhite,
              border: `1px solid ${modalStyles.borderSecondary}`,
              borderRadius: modalStyles.borderRadiusSmall,
              padding: modalStyles.paddingSmall,
              marginBottom: '16px',
              maxHeight: 'calc(100vh - 280px)',
              overflow: 'auto'
            }}>
              {/* Debug info for development */}
              {process.env.NODE_ENV === 'development' && parentRecords.length === 0 && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '12px',
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}>
                  <div><strong>Debug: No parent records found</strong></div>
                  <div>Please check the console for the actual API response structure</div>
                </div>
              )}

              <Table
                columns={parentColumns}
                dataSource={parentRecords}
                rowKey="inspectionId"
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 1100, y: 'calc(100vh - 350px)' }}
                locale={{
                  emptyText: parentRecords.length === 0 ? 'No receiving records found. Please check console for details.' : 'No data'
                }}
                expandable={{
                  expandedRowRender: (record: any) => (
                    <div style={{
                      margin: '8px 0',
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        marginBottom: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: modalStyles.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Items Details ({record.unloadingDetail?.length || 0})
                      </div>
                      <Table
                        columns={detailColumns}
                        dataSource={record.unloadingDetail || []}
                        rowKey={(detail: GetUnloadingDetailDto, index?: number) => detail.detailId || detail.rawMaterialId?.toString() || index?.toString() || 'row'}
                        pagination={false}
                        size="small"
                        bordered={false}
                        showHeader={true}
                        style={{
                          backgroundColor: 'transparent'
                        }}
                        rowClassName={(_, index) => index % 2 === 0 ? '' : 'table-row-alternate'}
                      />
                    </div>
                  ),
                  expandIcon: ({ expanded, onExpand, record }: any) => (
                    <div
                      onClick={e => onExpand(record, e)}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: expanded ? modalStyles.primary : '#f3f4f6',
                        color: expanded ? '#fff' : modalStyles.textSecondary,
                        fontSize: '12px',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                    >
                      {expanded ? '▼' : '▶'} {record.unloadingDetail?.length || 0} items
                    </div>
                  ),
                  defaultExpandAllRows: true,
                  expandRowByClick: false
                }}
              />
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: modalStyles.textSecondary }}>
            No data available
          </div>
        )}
      </div>

      {/* Image Preview */}
      <Image.PreviewGroup
        preview={{
          visible: previewVisible,
          onVisibleChange: (vis) => {
            setPreviewVisible(vis)
            if (!vis) setPreviewImage('')
          },
          current: parentRecords.flatMap((record: any) => parseImageUrls(record.images)).findIndex((url) => url === previewImage) || 0
        }}
      >
        {parentRecords.flatMap((record: any) =>
          parseImageUrls(record.images).map((url) => <Image key={url} src={url} style={{ display: 'none' }} />)
        )}
      </Image.PreviewGroup>
    </Modal>
  )
}

export default InspectionLogModal
